import { useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';

import { get as getUser } from 'js/lib/user';

import { getSkillPassingThresholdForAssessment } from 'bundles/assess-common/utils/skillAssessmentsUtils';
import GetSkillAreaCourseItemsQuery, {
  type GetSkillAreaCourseItemsQueryData,
  type GetSkillAreaCourseItemsQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetSkillAreaCourseItems';
import GetSkillExpressionAssessmentMappingsByAssessmentIdQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetSkillExpressionAssessmentMappingsByAssessmentId.graphql';
import type { SkillAttainmentSkillAreaAttainmentFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import type {
  GetSkillExpressionAssessmentMappingsByAssessmentIdQuery as GetSkillExpressionAssessmentMappingsByAssessmentIdQueryData,
  GetSkillExpressionAssessmentMappingsByAssessmentIdQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillExpressionAssessmentMappingsByAssessmentId';
import useGetSkillAssessmentIdAndPassingFraction from 'bundles/skills-common/shared/verified-skills/hooks/useGetSkillAssessmentIdAndPassingFraction';
import { getIsSkillAssessmentItem } from 'bundles/skills-common/shared/verified-skills/utils/verifiedSkillsUtils';

export type SkillPassingThresholdTotals = {
  hasMasteredSkillArea: boolean;
  skillAttainmentThreshold: number;
};

type Props = {
  skillAttainmentForCourse?: SkillAttainmentSkillAreaAttainmentFragment;
  courseId?: string;
};

/**
 * Custom hook that calculates the total and passing threshold for skill assessments.
 * Fetches thresholds for all assessments and calculates aggregated totals using
 * getSkillPassingThresholdForAssessment.
 * Use this hook when you need to fetch mastery details for a skill area with some already mastered skills. No mastered skills means the course might not be enrolled in yet.
 *
 * This is used inside the Skill Dialog and so must not rely on computed item data as Skill Dialogs are also rendered inside Enterprise pages.
 *
 * @param skillAttainmentForCourse - The skill attainment for the course.
 * @param courseId - The course ID.
 * @returns The skill mastery details.
 */
export const useGetSkillMasteryDetailsForSkillArea = ({ skillAttainmentForCourse, courseId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [totals, setTotals] = useState<SkillPassingThresholdTotals>({
    hasMasteredSkillArea: false,
    skillAttainmentThreshold: 0,
  });

  const userId = getUser().id;

  const {
    data,
    error: itemsError,
    loading: itemsLoading,
  } = useQuery<GetSkillAreaCourseItemsQueryData, GetSkillAreaCourseItemsQueryVariables>(GetSkillAreaCourseItemsQuery, {
    variables: {
      courseId: courseId || '',
      learnerId: `${userId}` || '',
    },
    fetchPolicy: 'cache-first',
  });

  const items = data?.OnDemandCourseMaterialsV2?.learner?.linked?.onDemandCourseMaterialItemsV2;

  const skillAssessmentItems = useMemo(
    () =>
      items
        ?.filter((item) =>
          getIsSkillAssessmentItem(
            item.contentSummary?.typeName,
            item.contentSummary?.definition?.assignmentFeatures ?? undefined
          )
        )
        .map((item) => ({
          itemId: item?.id || '',
          itemTypeName: item?.contentSummary?.typeName,
          passingFraction: item?.contentSummary?.definition?.passingFraction,
        })) || [],
    [items]
  );

  const { fetchSkillAssessmentIdAndPassingFraction } = useGetSkillAssessmentIdAndPassingFraction();

  const [fetchSkillExpressionMappings] = useLazyQuery<
    GetSkillExpressionAssessmentMappingsByAssessmentIdQueryData,
    GetSkillExpressionAssessmentMappingsByAssessmentIdQueryVariables
  >(GetSkillExpressionAssessmentMappingsByAssessmentIdQuery, {
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  // Instead of disabling the rule entirely, be explicit about stable references
  const fetchSkillExpressionMappingsRef = useRef(fetchSkillExpressionMappings);
  const fetchSkillAssessmentIdAndPassingFractionRef = useRef(fetchSkillAssessmentIdAndPassingFraction);

  useEffect(() => {
    fetchSkillExpressionMappingsRef.current = fetchSkillExpressionMappings;
    fetchSkillAssessmentIdAndPassingFractionRef.current = fetchSkillAssessmentIdAndPassingFraction;
  });

  useEffect(() => {
    let isCancelled = false;

    const fetchAllPassingThresholds = async () => {
      // Don't set loading to false if the items query is still loading
      // because we don't know yet if there will be items
      if (itemsLoading) {
        return;
      }

      if (!skillAssessmentItems || skillAssessmentItems.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Fetch assessment id + passing fraction by item type, then fetch skill expression mappings.
        const results = await Promise.all(
          skillAssessmentItems.map(async ({ itemId, itemTypeName, passingFraction }) => {
            const {
              assessmentId,
              assessmentType,
              passingFraction: fetchedPassingFraction,
            } = await fetchSkillAssessmentIdAndPassingFractionRef.current({
              itemId,
              itemTypeName,
              courseId: courseId || '',
            });

            if (!assessmentId) {
              throw new Error('Failed to fetch assessment id');
            }

            const skillExpressionsMappingResult = assessmentId
              ? await fetchSkillExpressionMappingsRef.current({
                  variables: {
                    assessmentId,
                    assessmentType,
                  },
                })
              : null;

            return {
              itemId,
              passingFraction: passingFraction ?? fetchedPassingFraction ?? 1,
              skillExpressionMappings:
                skillExpressionsMappingResult?.data?.VerifiedSkillsMapping
                  ?.getSkillExpressionAssessmentMappingsByAssessmentId,
            };
          })
        );

        if (isCancelled) return;

        // Calculate totals for each assessment and aggregate
        const calculatedTotals = results.reduce<SkillPassingThresholdTotals>(
          (acc, { passingFraction, skillExpressionMappings }, index) => {
            const mappedSkillExpressionIds = new Set(
              skillExpressionMappings?.map((mapping) => mapping.skillExpression.id) ?? []
            );

            const skillExpressionsAttainment =
              skillAttainmentForCourse?.skillExpressionsAttainment?.filter((attainment) =>
                mappedSkillExpressionIds.has(attainment?.skillExpression?.id ?? '')
              ) || [];

            const assessmentResult = getSkillPassingThresholdForAssessment({
              skillExpressionsAttainment,
              passingFraction,
            });

            return {
              hasMasteredSkillArea:
                index === 0 ? assessmentResult.isPassed : acc.hasMasteredSkillArea && assessmentResult.isPassed,
              skillAttainmentThreshold: acc.skillAttainmentThreshold + assessmentResult.skillAttainmentThreshold,
            };
          },
          {
            hasMasteredSkillArea: false,
            skillAttainmentThreshold: 0,
          }
        );

        setTotals(calculatedTotals);
      } catch (err) {
        setTotals({
          hasMasteredSkillArea: false,
          skillAttainmentThreshold: 1,
        });
        setError(err as Error);
        Sentry.captureException(err as Error, {
          extra: {
            message: 'Failed to fetch skill mastery details and passing thresholds for skill area',
            courseId,
            skillAreaId: skillAttainmentForCourse?.skillArea?.id,
          },
        });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchAllPassingThresholds();

    return () => {
      isCancelled = true;
    };
  }, [courseId, skillAttainmentForCourse, itemsLoading, skillAssessmentItems]);

  const total = skillAssessmentItems?.length;
  const isLoading = itemsLoading || loading;

  return {
    total,
    loading: isLoading,
    error: itemsError || error,
    hasMasteredSkillArea: isLoading ? false : totals.hasMasteredSkillArea,
    skillAttainmentThreshold: isLoading ? 0 : totals.skillAttainmentThreshold,
  };
};
